// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { object, func, bool, string, array } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { uniqueId } from 'lodash';
import { CircleHelp, CircleCheck, CircleX, CircleAlert } from 'lucide-react';
import {
  Button,
  Loading,
  SkeletonPlaceholder,
  SkeletonText,
  ComposedModal,
  ModalHeader,
  ModalBody
} from '@carbon/react';

import { NWTextInput, NWNotification } from 'Components/shared';

const StyledAccountProfile = styled.div`
  section {
    margin-bottom: 16px;
    border: 1px solid #e2e2e2;

    h4:not(.cds--modal h4) {
      font-size: 16px;
      padding: 16px;
      background: #f5f7f9;
    }

    .sectionBody {
      display: flex;
      gap: 16px;
      padding: 16px;

      &.column {
        flex-direction: column;
      }

      &.consent {
        > div > div {
          margin: 0;
        }
      }

      &.mfa {
        .mfaMethod {
          padding: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;

          &.active {
            border: 1px solid #498246;
          }

          &.disabled {
            color: #8d8d8d;

            svg {
              stroke: #8d8d8d;
            }
          }

          .mfaMethodInfo {
            display: flex;
            align-items: center;
            gap: 8px;

            .mfaTitle {
              display: flex;
              align-items: center;
              gap: 8px;
              font-weight: 600;
            }
          }

          svg {
            stroke: #498246;
          }
        }

        button  {
          width: 100%;
          max-width: 200px;
          height: 40px !important;
          border: 1px solid #1F59A2;
          color: #1F59A2;
        }
      }

      &.skeleton {
        flex-direction: column;
        
        > div {
          flex: auto;
        }
      }

      > div {
        flex: 1;

        .emailContainer {
          position: relative;

          > svg {
            position: absolute;
            top: 30px;
            right: 15px;
            z-index: 1;
          }

          .react-tooltip {
            max-width: 300px;
          }
        }
      }
    }
  }

  .submit {
    display: flex;
    justify-content: end;
    padding-bottom: 24px;

    button {
      width: 100%;
      max-width: 255px;
      height: 40px !important;
      background-color: #da1e28;
    }
  }

  button svg {
    height: 16px;
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .sectionBody {
      flex-direction: column;
    }

    section {
      .sectionBody.mfa {
        .mfaMethod {
          flex-direction: column;
          gap: 20px;

          .mfaMethodInfo {
            flex-direction: column;
            gap: 12px;
          }
        }

        button  {
          max-width: none;
        }
      }
    }

    .submit {
      button {
        width: 100%;
        max-width: none;
      }
    }
  }
`;

const StyledModal = styled(ComposedModal)`
  .cds--modal-content {
    padding: 0;
    margin: 0;
  }

  .body {
    margin-bottom: 20px;

    .message {
      padding: 0 24px;

      p {
        font-size: 16px;
        line-height: 21px;
        margin: 12px 0;
        padding: 0;
      }
    }
  }

  .modalError {
    padding: 0 80px;
  }

  .footer {
    padding: 0 24px 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-direction: column;

    .reason {
      margin-bottom: 0;
      width: 100%;
    }

    .danger {
      width: 100%;
      max-width: none;
    }
  }
`;

const StyledButton = styled(Button)`
  width: 200px;
  height: 40px;
  padding: 0;
  justify-content: center;

  &.primary {
    border: 1px solid #1f59a2;
    color: #1f59a2;
  }

  &.danger {
    background-color: #da1e28;
    border: 1px solid transparent;
    color: #fff;
  }
`;

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  height: 3rem;
  width: 100%;
`;

const fields = {
  email: 'email',
  oldPassword: 'oldPassword',
  password: 'password',
  confirmPassword: 'confirmPassword',
};

const AccountProfile = ({
  loading,
  isAdmin,
  validatorsEmail,
  registerEmail,
  rhfErrorsEmail,
  validators,
  register,
  rhfErrors,
  handleOnSubmit,
  handleOnSubmitPassword,
  submitLoading,
  submitPasswordLoading,
  registerReason,
  validatorsReason,
  reasonValue,
  rhfErrorsReason,
  modalOpen,
  onModalClose,
  onModalOpen,
  disableMFAloading,
  disableMFAError,
  handleMFADisable,
  isTotpEnabled,
  isImpersonated,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const sharedPropsEmail = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrorsEmail[fieldName]?.message.length,
    invalidText: rhfErrorsEmail[fieldName]?.message,
    ...registerEmail(fieldName, { ...validatorsEmail[fieldName] }),
  });

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  return (
    <StyledAccountProfile theme={ theme }>
      { loading ? (
        <>
          <section>
            <h4><SkeletonText /></h4>
            <div className="sectionBody skeleton">
              { Array.from({ length: 3 }, () => <StyledSkeletonPlaceholder key={ uniqueId('skeletonPlaceholder') } />)}
            </div>
          </section>
          { Array.from({ length: 2 }, () => (
            <section key={ uniqueId('skeletonPlaceholder') }>
              <h4><SkeletonText /></h4>
              <div className="sectionBody skeleton">
                <SkeletonText paragraph />
              </div>
            </section>
          ))}
        </>
      ) : (
        <>
          <section>
            <h4>{ t('profile.authenticationData') }</h4>
            <div className="sectionBody">
              <div>
                <div className="emailContainer">
                  { (isAdmin) && (
                    <>
                      <CircleHelp size={ 20 } id="emailTooltip" />
                      <ReactTooltip
                        className="emailTooltip"
                        anchorId="emailTooltip"
                        variant="light"
                        offset={ 10 }
                        content={ (
                          <p>{ t('orders.emailChangeInfo') }</p>
                        ) }
                      />
                    </>
                  ) }
                  <NWTextInput
                    light
                    required
                    readOnly={ isAdmin }
                    labelText={ t(`pages.requestForm.${ fields.email }`) }
                    { ...sharedPropsEmail(fields.email) }
                  />
                </div>
              </div>
            </div>
          </section>
          { !isAdmin && (
            <div className="submit">
              <Button
                className="primary"
                type="submit"
                disabled={ loading }
                onClick={ handleOnSubmit }
              >
                { submitLoading && <Loading active small withOverlay={ false } /> }
                { t('pages.save') }
              </Button>
            </div>
          ) }
          <section>
            <StyledModal
              open={ modalOpen }
              size="sm"
              onClose={ onModalClose }
              selectorPrimaryFocus=".cds--modal-content"
            >
              <ModalHeader>
                <h4>{ t('profile.resetMFAApp') }</h4>
              </ModalHeader>
              <ModalBody>
                <div className="body">
                  <div className="message">
                    <p>{ t('profile.resetMFAAppInfo') }</p>
                  </div>
                </div>
                { disableMFAError && (
                  <div className="modalError">
                    <NWNotification errors={ disableMFAError } />
                  </div>
                ) }
                <div className="footer">
                  <NWTextInput
                    className="reason"
                    light
                    required
                    labelText={ t('profile.enterReason') }
                    id="reason"
                    name="fieldName"
                    invalid={ !!rhfErrorsReason?.reason?.message.length }
                    invalidText={ rhfErrorsReason.reason?.message }
                    { ...registerReason('reason', { ...validatorsReason.reason }) }
                  />
                  <StyledButton
                    onClick={ handleMFADisable }
                    className="danger"
                    disabled={ disableMFAloading || !reasonValue?.length || rhfErrorsReason.reason?.message.length }
                  >
                    { disableMFAloading && <Loading active small withOverlay={ false } /> }
                    { t('common.reset') }
                  </StyledButton>
                </div>
              </ModalBody>
            </StyledModal>
            <h4>{ t('profile.twoStepsAuthentication') }</h4>
            <div className="sectionBody column mfa">
              <div className={ `mfaMethod ${ isTotpEnabled ? 'active' : ''}` }>
                <div className="mfaMethodInfo">
                  <span className="mfaTitle">
                    { isTotpEnabled ? <CircleCheck /> : <CircleX />}
                    { t('profile.authenticationThroughApp')}
                  </span>
                </div>
                <Button
                  id={ isImpersonated ? 'isImpersonatedTooltip' : undefined }
                  className="primary"
                  kind="ghost"
                  type="button"
                  disabled={ !isTotpEnabled || isImpersonated }
                  onClick={ onModalOpen }
                >
                  { isTotpEnabled ? t('common.reset') : t('common.activate') }
                </Button>
                <ReactTooltip
                  className="isImpersonatedTooltip"
                  anchorId="isImpersonatedTooltip"
                  variant="light"
                  offset={ 10 }
                  content={ (
                    <p>{ t('orders.impersonatedResetRestriction') }</p>
                  ) }
                />
              </div>
              <div className="mfaMethod disabled">
                <div className="mfaMethodInfo">
                  <span className="mfaTitle">
                    <CircleAlert />
                    { t('profile.authenticationThroughSMS')}
                  </span>
                </div>
              </div>
            </div>
          </section>
          <section>
            <h4>{ t('profile.passwordReset') }</h4>
            <div className="sectionBody">
              <div>
                <NWTextInput
                  light
                  autoComplete="new-password"
                  type="password"
                  labelText={ t(`pages.requestForm.${ fields.oldPassword }`) }
                  { ...sharedProps(fields.oldPassword) }
                />
              </div>
              <div>
                <NWTextInput
                  light
                  autoComplete="new-password"
                  type="password"
                  labelText={ t('pages.requestForm.newPassword') }
                  { ...sharedProps(fields.password) }
                />
                <NWTextInput
                  light
                  autoComplete="new-password"
                  type="password"
                  labelText={ t(`pages.requestForm.${ fields.confirmPassword }`) }
                  { ...sharedProps(fields.confirmPassword) }
                />
              </div>
            </div>
          </section>
          <div className="submit">
            <Button
              className="primary"
              type="submit"
              disabled={ loading }
              onClick={ handleOnSubmitPassword }
            >
              { submitPasswordLoading && <Loading active small withOverlay={ false } /> }
              { t('pages.save') }
            </Button>
          </div>
        </>
      )}
    </StyledAccountProfile>
  );
};

AccountProfile.propTypes = {
  loading: bool,
  isAdmin: bool,
  validatorsEmail: object,
  registerEmail: func,
  rhfErrorsEmail: object,
  validators: object,
  register: func,
  rhfErrors: object,
  handleOnSubmit: func,
  handleOnSubmitPassword: func,
  submitLoading: bool,
  submitPasswordLoading: bool,
  registerReason: func,
  validatorsReason: object,
  reasonValue: string,
  rhfErrorsReason: object,
  modalOpen: bool,
  onModalClose: func,
  disableMFAloading: bool,
  disableMFAError: array,
  onModalOpen: func,
  handleMFADisable: func,
  isTotpEnabled: bool,
  isImpersonated: bool,
};

AccountProfile.displayName = 'AccountProfileComponent';

export default AccountProfile;
